<template>
  <div>
    <Head title="Panel de Administración" />

    <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
      <h1 class="text-3xl font-bold mb-8">Panel de Administración</h1>

      <!-- Tarjetas de estadísticas -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <!-- Usuarios -->
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-blue-100 text-blue-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div class="ml-4">
              <h2 class="text-lg font-semibold text-gray-700">Usuarios</h2>
              <p class="text-2xl font-bold text-gray-900">{{ totalUsers }}</p>
              <p class="text-sm text-gray-500">{{ newUsersToday }} nuevos hoy</p>
            </div>
          </div>
        </div>

        <!-- Cuentas -->
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-green-100 text-green-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div class="ml-4">
              <Link href="/admin/accounts">
                <h2 class="text-lg font-semibold text-gray-700">Cuentas</h2>
              </Link>
              <p class="text-2xl font-bold text-gray-900">{{ totalAccounts }}</p>
              <p class="text-sm text-gray-500">{{ paidAccounts }} planes pagados</p>
            </div>
          </div>
        </div>

        <!-- Ingresos -->
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-purple-100 text-purple-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="ml-4">
              <h2 class="text-lg font-semibold text-gray-700">Ingresos Mensuales</h2>
              <p class="text-2xl font-bold text-gray-900">${{ monthlyRevenue.toFixed(2) }}</p>
              <p class="text-sm text-gray-500">Ingresos totales</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Gráficos y tablas -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Gráfico de usuarios -->
        <div class="bg-white rounded-lg shadow p-6">
          <h2 class="text-xl font-semibold mb-4">Crecimiento de Usuarios</h2>
          <div class="h-64">
            <!-- Aquí iría el gráfico de usuarios -->
            <div class="flex items-center justify-center h-full text-gray-500">
              Gráfico de usuarios (pendiente de implementación)
            </div>
          </div>
        </div>

        <!-- Gráfico de ingresos -->
        <div class="bg-white rounded-lg shadow p-6">
          <h2 class="text-xl font-semibold mb-4">Ingresos Mensuales</h2>
          <div class="h-64">
            <!-- Aquí iría el gráfico de ingresos -->
            <div class="flex items-center justify-center h-full text-gray-500">
              Gráfico de ingresos (pendiente de implementación)
            </div>
          </div>
        </div>

        <!-- Usuarios recientes -->
        <div class="bg-white rounded-lg shadow p-6">
          <h2 class="text-xl font-semibold mb-4">Usuarios Recientes</h2>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha de Registro</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="user in recentUsers" :key="user.id">
                  <td class="px-6 py-4 whitespace-nowrap">{{ user.first_name }} {{ user.last_name }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">{{ user.email }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">{{ user.account.plan_name }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">{{ formatDate(user.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3';
import Layout from '@/Shared/Layout';

export default {
  components: {
    Head,
    Link
  },

  layout: Layout,

  props: {
    totalUsers: Number,
    newUsersToday: Number,
    recentUsers: Array,
    totalAccounts: Number,
    paidAccounts: Number,
    monthlyRevenue: Number,
  },

  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    }
  }
}
</script> 